import { createClient } from '@supabase/supabase-js';
import {
  isDirectEnvAvailable,
  isProxyEnvAvailable,
  readProxyEnabledFromStorage,
} from '../utils/supabaseProxySetting';
import { getProxySessionToken } from './proxySessionToken';

let connectionMode = 'direct';
let cachedClients = null;
let clientInitError = null;

function createProxyClients() {
  const proxyUrl = (process.env.REACT_APP_SUPABASE_PROXY_URL || '').trim().replace(/\/$/, '');
  if (!proxyUrl) {
    throw new Error(
      'Proxy Supabase: brak REACT_APP_SUPABASE_PROXY_URL w .env / Vercel.'
    );
  }
  const realFetch = typeof window !== 'undefined' ? window.fetch.bind(window) : fetch;
  const proxyFetch = (input, init) => {
    const url = typeof input === 'string' ? input : input && input.url;
    if (!url || !url.startsWith(proxyUrl)) return realFetch(input, init);
    const pathWithQuery = url.slice(proxyUrl.length) || '/';
    const opts =
      typeof input === 'string'
        ? { ...init }
        : {
            method: input.method,
            headers: input.headers,
            body: input.body,
            mode: input.mode,
            credentials: input.credentials,
          };
    const headers = new Headers(opts?.headers || {});
    headers.set('X-Supabase-Path', pathWithQuery);
    const sessionToken = getProxySessionToken();
    if (sessionToken) {
      headers.set('X-Proxy-Token', sessionToken);
    }
    return realFetch(proxyUrl, { ...opts, headers });
  };
  const proxyClient = createClient(proxyUrl, 'proxy', { global: { fetch: proxyFetch } });
  return { supabase: proxyClient, supabaseAdmin: proxyClient };
}

function createDirectClients() {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const supabaseServiceRoleKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY;
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Połączenie bezpośrednie: ustaw REACT_APP_SUPABASE_URL i REACT_APP_SUPABASE_ANON_KEY w .env.'
    );
  }
  const clientSupabase = createClient(supabaseUrl, supabaseAnonKey);
  const clientSupabaseAdmin = supabaseServiceRoleKey
    ? createClient(supabaseUrl, supabaseServiceRoleKey)
    : clientSupabase;
  return { supabase: clientSupabase, supabaseAdmin: clientSupabaseAdmin };
}

function buildClients() {
  const useProxyPref = readProxyEnabledFromStorage();
  const proxyOk = isProxyEnvAvailable();
  const directOk = isDirectEnvAvailable();

  if (useProxyPref && proxyOk) {
    connectionMode = 'proxy';
    return createProxyClients();
  }
  if (!useProxyPref && directOk) {
    connectionMode = 'direct';
    return createDirectClients();
  }
  if (proxyOk) {
    connectionMode = 'proxy';
    return createProxyClients();
  }
  if (directOk) {
    connectionMode = 'direct';
    return createDirectClients();
  }

  throw new Error(
    'Brak konfiguracji Supabase. Na Vercel ustaw REACT_APP_SUPABASE_PROXY_URL (produkcja) lub lokalnie URL + anon key.'
  );
}

function getClients() {
  if (cachedClients) {
    return cachedClients;
  }
  if (clientInitError) {
    throw clientInitError;
  }
  try {
    cachedClients = buildClients();
    clientInitError = null;
    return cachedClients;
  } catch (err) {
    clientInitError = err;
    throw err;
  }
}

/** Błąd konfiguracji przy starcie (np. brak zmiennych na Vercel) — do wyświetlenia w UI. */
export function getSupabaseInitError() {
  try {
    getClients();
    return null;
  } catch (err) {
    return err?.message || String(err);
  }
}

function createLazyClient(which) {
  return new Proxy(
    {},
    {
      get(_target, prop) {
        const client = getClients()[which];
        const value = client[prop];
        if (typeof value === 'function') {
          return value.bind(client);
        }
        return value;
      },
    }
  );
}

export const supabase = createLazyClient('supabase');
export const supabaseAdmin = createLazyClient('supabaseAdmin');

export function getSupabaseConnectionMode() {
  try {
    getClients();
  } catch {
    // ignore
  }
  return connectionMode;
}

export function isSupabaseProxyModeActive() {
  return getSupabaseConnectionMode() === 'proxy';
}
